import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import type {
  DocumentEntityType,
  DocumentVersionDetails,
  UploadedDocument,
} from '../form-builder/types';

/**
 * Minimal HTTP client surface this module needs. Lets us share the
 * implementation between the backoffice `apiClient` (custom wrapper class)
 * and the candidate-portal `inviteApiClient` (raw `AxiosInstance`) without
 * coupling either to the other.
 */
export interface HttpClient {
  get<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>>;
  post<T = unknown>(
    url: string,
    body?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>>;
}

/**
 * Shared client-agnostic implementation of the versioned document API
 * documented in `docs/onboarding/API/Document-Upload.md`. The auth mode is
 * decided by the caller's axios instance (backoffice `apiClient` for JWT,
 * `inviteApiClient` for invite-token).
 *
 * Endpoint base: `/console/user_onboarding/invite/document/:entity_type/:entity_id/...`
 *   - `entity_type ∈ {case, case_id, user, rgf_id}` (normalized server-side).
 *   - The body field name MUST be `file` (single file per request); multi-file
 *     UIs fire N parallel requests.
 */

export interface UploadDocumentArgs {
  entityType: DocumentEntityType;
  entityId: number | string;
  documentTypeId: number;
  file: File;
  signal?: AbortSignal;
  /** Override upload timeout (ms). Defaults to 2 min, large enough for slow connections. */
  timeoutMs?: number;
}

export interface GetDocumentVersionArgs {
  entityType: DocumentEntityType;
  entityId: number | string;
  versionId: number;
  signal?: AbortSignal;
}

/**
 * Error subclass used for the documented `409` "submission already under
 * review" case. Carries the existing `submission_id` so the UI can offer
 * actionable copy (and optionally deep-link to the pending submission).
 */
export class DocumentPendingReviewError extends Error {
  readonly pendingSubmissionId: number | undefined;
  readonly status: string | undefined;
  constructor(message: string, pendingSubmissionId?: number, status?: string) {
    super(message);
    this.name = 'DocumentPendingReviewError';
    this.pendingSubmissionId = pendingSubmissionId;
    this.status = status;
  }
}

const buildUploadUrl = (entityType: DocumentEntityType, entityId: number | string): string =>
  `${apiConfig.endpoints.userOnboarding.inviteDocument}/${encodeURIComponent(
    entityType,
  )}/${encodeURIComponent(String(entityId))}/upload`;

const buildVersionUrl = (
  entityType: DocumentEntityType,
  entityId: number | string,
  versionId: number,
): string =>
  `${apiConfig.endpoints.userOnboarding.inviteDocument}/${encodeURIComponent(
    entityType,
  )}/${encodeURIComponent(String(entityId))}/version/${encodeURIComponent(String(versionId))}`;

const unwrapServerMessage = (data: unknown): string | null => {
  if (data && typeof data === 'object') {
    const o = data as Record<string, unknown>;
    if (typeof o.message === 'string') return o.message;
    if (typeof o.error === 'string') return o.error;
  }
  return null;
};

const mapUploadError = (error: any, fallback: string): never => {
  if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
    throw error;
  }
  const status = error?.response?.status;
  const data = error?.response?.data;
  const message = unwrapServerMessage(data) || error?.message || fallback;

  if (status === 409) {
    const submissionId =
      typeof data?.submission_id === 'number' ? (data.submission_id as number) : undefined;
    const submissionStatus = typeof data?.status === 'string' ? (data.status as string) : undefined;
    throw new DocumentPendingReviewError(message, submissionId, submissionStatus);
  }

  const wrapped = new Error(message);
  (wrapped as any).status = status;
  (wrapped as any).serverError = data;
  throw wrapped;
};

const mapGetError = (error: any, fallback: string): never => {
  if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
    throw error;
  }
  const status = error?.response?.status;
  const data = error?.response?.data;
  const message = unwrapServerMessage(data) || error?.message || fallback;
  const wrapped = new Error(message);
  (wrapped as any).status = status;
  (wrapped as any).serverError = data;
  throw wrapped;
};

interface UploadResponseEnvelope {
  data?: {
    document_submission_id?: unknown;
    document_version_id?: unknown;
    submission_id?: unknown;
    version_id?: unknown;
  };
  document_submission_id?: unknown;
  document_version_id?: unknown;
  submission_id?: unknown;
  version_id?: unknown;
  [key: string]: unknown;
}

/**
 * Two doc snippets disagree on the upload response envelope:
 *   - `Document-Upload.md` §1:  `{ data: { document_submission_id, document_version_id } }`
 *   - `PDF-Resource-Type.md` §2: `{ submission_id, version_id, status }`
 *
 * They describe the same endpoint, so the server returns one shape — but
 * until backend confirms which, this parser accepts either. The keys checked
 * (in order): nested `data.document_*`, nested `data.{submission,version}_id`,
 * top-level `document_*`, top-level `{submission,version}_id`.
 */
const parseUploadResponse = (response: AxiosResponse<UploadResponseEnvelope>): {
  submissionId: number;
  versionId: number;
} => {
  const root = response?.data ?? {};
  const inner = (root.data ?? {}) as Record<string, unknown>;
  const top = root as unknown as Record<string, unknown>;

  const pickPositiveInt = (...candidates: unknown[]): number | null => {
    for (const c of candidates) {
      const n = Number(c);
      if (Number.isFinite(n) && Number.isInteger(n) && n > 0) return n;
    }
    return null;
  };

  const submissionId = pickPositiveInt(
    inner.document_submission_id,
    inner.submission_id,
    top.document_submission_id,
    top.submission_id,
  );
  const versionId = pickPositiveInt(
    inner.document_version_id,
    inner.version_id,
    top.document_version_id,
    top.version_id,
  );
  if (submissionId === null) {
    throw new Error('Upload succeeded but document_submission_id was missing in the response.');
  }
  if (versionId === null) {
    throw new Error('Upload succeeded but document_version_id was missing in the response.');
  }
  return { submissionId, versionId };
};

/**
 * Upload one file as a new version of `(entity, document_type)`.
 *
 * Multi-file pickers should call this once per file in parallel — the API
 * accepts a single `file` part per request.
 */
export const uploadDocument = async (
  client: HttpClient,
  args: UploadDocumentArgs,
): Promise<UploadedDocument> => {
  const { entityType, entityId, documentTypeId, file, signal, timeoutMs = 120_000 } = args;
  if (!Number.isFinite(documentTypeId) || documentTypeId <= 0) {
    throw new Error(
      'document_type_id is required and must be a positive integer for versioned document uploads.',
    );
  }
  const body = new FormData();
  body.append('file', file, file.name);
  body.append('document_type_id', String(documentTypeId));

  let response: AxiosResponse<UploadResponseEnvelope>;
  try {
    response = await client.post<UploadResponseEnvelope>(
      buildUploadUrl(entityType, entityId),
      body,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: timeoutMs,
        signal,
      },
    );
  } catch (error) {
    mapUploadError(error, 'Could not upload that file.');
  }
  const { submissionId, versionId } = parseUploadResponse(response!);
  return {
    document_submission_id: submissionId,
    document_version_id: versionId,
    filename: file.name,
    mime_type: file.type || undefined,
  };
};

/** Fetch presigned URL + metadata for one version. */
export const getDocumentVersion = async (
  client: HttpClient,
  args: GetDocumentVersionArgs,
): Promise<DocumentVersionDetails> => {
  const { entityType, entityId, versionId, signal } = args;
  if (!Number.isFinite(versionId) || versionId <= 0) {
    throw new Error('versionId must be a positive integer.');
  }
  try {
    const { data } = await client.get<DocumentVersionDetails>(
      buildVersionUrl(entityType, entityId, versionId),
      { signal },
    );
    return data;
  } catch (error) {
    mapGetError(error, 'Could not load that document.');
  }
  // Unreachable — mapGetError always throws.
  throw new Error('unreachable');
};
